import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { typography } from 'styled-system';

import { TextProps } from 'src/packages/core/text/Text.types';
import { SpacerSpan } from 'src/packages/core';
import { LightTheme, DarkTheme } from 'src/packages/theme';

/*const rtlCSS = css`
  direction: rtl;
  unicode-bidi: bidi-override;
`;*/

// add this in StyledText
/*${(props) => (props.theme.rtl && !props.overrideRtl ? rtlCSS : null)};*/

const StyledText = styled((props) => <SpacerSpan {...props} />)<TextProps>`
  ${({
    defaulttheme,
    inline,
    variant,
    fontFamily,
    textAlign,
    textTransform,
    textDecoration,
    color,
  }) => `
    display: ${inline ? 'inline' : 'inline-block'};
    font-size: ${defaulttheme.typographyScale.desktop[variant!].fontSize};
    font-weight: ${defaulttheme.typographyScale.desktop[variant!].fontWeight};
    line-height: ${defaulttheme.typographyScale.desktop[variant!].lineHeight};
      font-family: ${fontFamily ?? defaulttheme.typographyScale.desktop[variant!].fontFamily};
      text-transform: ${textTransform};
      text-decoration: ${textDecoration};
      color: ${
        color
          ? color
          : ['p1', 'p2', 'p3'].includes(variant)
          ? defaulttheme.colors.text.secondary
          : defaulttheme.colors.text.primary
      };
      ${typography};
      direction: ltr;
      unicode-bidi: bidi-override;
      height: fit-content;
    
      text-align: ${textAlign};
    
      ${defaulttheme.mediaQueries.md} {
        font-size: ${defaulttheme.typographyScale.mobile[variant!].fontSize};
        font-weight: ${defaulttheme.typographyScale.mobile[variant!].fontWeight};
        line-height: ${defaulttheme.typographyScale.mobile[variant!].lineHeight};
        font-family: ${defaulttheme.typographyScale.mobile[variant!].fontFamily};
      }
      i {
        ${
          ['d1', 'd2', 'h1', 'h2', 'h3'].includes(variant)
            ? `
              background: ${defaulttheme.colors.palette.brand};
              background-clip: text;
              color: transparent;
              font-style: normal;
            `
            : ``
        }
      }
  `}
`;

const Text: React.FC<TextProps> = ({
  theme,
  inline = false,
  overrideRtl = false,
  width = 'fit-content',
  variant = 'p1',
  text = '',
  children = undefined,
  className = '',
  textAlign = 'left',
  textTransform = 'none',
  textDecoration = 'none',
  style = {},
  ...rest
}): React.ReactElement => {
  const content = children ? children : !Number.isNaN(text) ? text : '';
  const contextTheme = useTheme();

  const defaultTheme = useMemo(() => {
    return theme
      ? theme === 'light'
        ? { ...LightTheme, rtl: contextTheme.rtl }
        : { ...DarkTheme, rtl: contextTheme.rtl }
      : contextTheme;
  }, [contextTheme, theme]);

  return (
    <StyledText
      variant={variant}
      className={className}
      width={width}
      inline={inline}
      overrideRtl={overrideRtl}
      color={rest.color}
      textAlign={textAlign}
      textTransform={textTransform}
      textDecoration={textDecoration}
      style={style}
      defaulttheme={defaultTheme}
      {...rest}
    >
      {content}
    </StyledText>
  );
};

export default Text;
